"use client";

import { PropertyContextCard } from "@/components/property-analysis/property-context-card";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { PropertyListing } from "@/lib/ratespot";
import { formatCurrency } from "@/lib/utils";
import { ArrowDownIcon, ArrowUpIcon, Info } from "lucide-react";

interface CompsSectionProps {
  comps: PropertyListing[];
  stats: {
    averagePrice: number;
    medianPrice: number;
    pricePerSquareFoot: number;
  };
  subjectPrice: number;
  subjectPricePerSquareFoot: number;
  subjectProperty: {
    beds: number;
    baths: number;
    sqft: number;
    daysOnMarket: number;
  };
}

function PricePerSqFtComparison({
  compPricePerSqft,
  subjectPricePerSquareFoot,
}: {
  compPricePerSqft: number | null;
  subjectPricePerSquareFoot: number;
}) {
  if (!compPricePerSqft) return <span>N/A</span>;
  if (subjectPricePerSquareFoot === 0)
    return <span>{formatCurrency(compPricePerSqft)}</span>;

  const difference =
    ((compPricePerSqft - subjectPricePerSquareFoot) /
      subjectPricePerSquareFoot) *
    100;
  const isHigher = compPricePerSqft > subjectPricePerSquareFoot;

  return (
    <div className="flex items-center gap-1">
      <span>{formatCurrency(compPricePerSqft)}</span>
      <span
        className={`flex items-center ${isHigher ? "text-red-500" : "text-green-500"}`}
      >
        {isHigher ? (
          <ArrowUpIcon className="h-4 w-4" />
        ) : (
          <ArrowDownIcon className="h-4 w-4" />
        )}
        <span className="text-xs">{Math.abs(difference).toFixed(1)}%</span>
      </span>
    </div>
  );
}

function ComparisonIndicator({
  value,
  compareValue,
  reverseColors = false,
  percentFormat = true,
}: {
  value: number;
  compareValue: number;
  reverseColors?: boolean;
  percentFormat?: boolean;
}) {
  // Return nothing if compareValue is zero to avoid infinity
  if (compareValue === 0) return null;

  const difference = ((value - compareValue) / compareValue) * 100;
  const isHigher = value > compareValue;

  // Determine color based on the comparison and reverseColors flag
  const displayColor = reverseColors
    ? isHigher
      ? "text-green-500"
      : "text-red-500"
    : isHigher
      ? "text-red-500"
      : "text-green-500";

  return (
    <div className="flex items-center gap-1">
      <span className={`flex items-center ${displayColor}`}>
        {isHigher ? (
          <ArrowUpIcon className="h-4 w-4" />
        ) : (
          <ArrowDownIcon className="h-4 w-4" />
        )}
        <span className="text-xs">
          {percentFormat
            ? `${Math.abs(difference).toFixed(1)}%`
            : Math.abs(value - compareValue).toFixed(0)}
        </span>
      </span>
    </div>
  );
}

export function CompsSection({
  comps,
  stats,
  subjectPrice,
  subjectPricePerSquareFoot,
  subjectProperty,
}: CompsSectionProps) {
  if (!comps?.length) return null;

  // Calculate differences between subject property and comps with safeguards
  const priceVsAvg =
    stats.averagePrice === 0
      ? 0
      : ((subjectPrice - stats.averagePrice) / stats.averagePrice) * 100;

  const priceVsMedian =
    stats.medianPrice === 0
      ? 0
      : ((subjectPrice - stats.medianPrice) / stats.medianPrice) * 100;

  const pricePerSqftVsAvg =
    stats.pricePerSquareFoot === 0
      ? 0
      : ((subjectPricePerSquareFoot - stats.pricePerSquareFoot) /
          stats.pricePerSquareFoot) *
        100;

  return (
    <section id="property-comps" className="space-y-6">
      {/* Property Context Card */}
      <PropertyContextCard
        price={subjectPrice}
        pricePerSqft={subjectPricePerSquareFoot}
        beds={subjectProperty.beds}
        baths={subjectProperty.baths}
        sqft={subjectProperty.sqft}
        daysOnMarket={subjectProperty.daysOnMarket}
        variant="compact"
        className="mb-4"
      />

      <div className="flex flex-col gap-4">
        <div>
          <h2 className="text-xl font-semibold">Stats in the Area</h2>
          <p className="text-sm text-gray-500 mt-1">
            Based on {comps.length} comparable properties nearby
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Average Price</p>
                <div className="flex items-center gap-2">
                  <p className="text-2xl font-semibold">
                    {stats.averagePrice === 0
                      ? "N/A"
                      : formatCurrency(stats.averagePrice)}
                  </p>
                  <ComparisonIndicator
                    value={subjectPrice}
                    compareValue={stats.averagePrice}
                  />
                </div>
              </div>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>
                    Average price of comparable properties in the area
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              {stats.averagePrice === 0 ? (
                "No average price data available"
              ) : (
                <>
                  Your price is {Math.abs(priceVsAvg).toFixed(1)}%{" "}
                  {priceVsAvg > 0 ? "above" : "below"} average
                </>
              )}
            </p>
          </Card>

          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Median Price</p>
                <div className="flex items-center gap-2">
                  <p className="text-2xl font-semibold">
                    {stats.medianPrice === 0
                      ? "N/A"
                      : formatCurrency(stats.medianPrice)}
                  </p>
                  <ComparisonIndicator
                    value={subjectPrice}
                    compareValue={stats.medianPrice}
                  />
                </div>
              </div>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>
                    Middle price point of comparable properties
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Based on {comps.length} recent sales
            </p>
          </Card>

          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Price per Sq.Ft</p>
                <div className="flex items-center gap-2">
                  <p className="text-2xl font-semibold">
                    {stats.pricePerSquareFoot === 0
                      ? "N/A"
                      : formatCurrency(stats.pricePerSquareFoot)}
                  </p>
                  <ComparisonIndicator
                    value={subjectPricePerSquareFoot}
                    compareValue={stats.pricePerSquareFoot}
                  />
                </div>
              </div>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>
                    Average price per square foot in the area
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              {stats.pricePerSquareFoot === 0 ? (
                "No price per sq.ft data available"
              ) : (
                <>
                  Your price is {Math.abs(pricePerSqftVsAvg).toFixed(1)}%{" "}
                  {pricePerSqftVsAvg > 0 ? "above" : "below"} average $/sqft
                </>
              )}
            </p>
          </Card>
        </div>

        <h2 className="text-xl font-semibold">Comparable Properties</h2>

        {/* Comps Table */}
        <Card>
          <div className="overflow-x-auto">
            {/* Desktop Table */}
            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Address</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Sq.Ft</TableHead>
                    <TableHead>Beds</TableHead>
                    <TableHead>Baths</TableHead>
                    <TableHead>$/Sq.Ft</TableHead>
                    <TableHead>Sale Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {comps.map((comp) => {
                    const price =
                      comp.pricing.sold?.amount || comp.pricing.sale?.amount;
                    const sqft = comp.facets.homeSize?.value;
                    const pricePerSqft = price && sqft ? price / sqft : null;

                    return (
                      <TableRow key={comp.id}>
                        <TableCell className="font-medium">
                          {comp.address.displayAddress}
                        </TableCell>
                        <TableCell>
                          {price ? formatCurrency(price) : "N/A"}
                        </TableCell>
                        <TableCell>{sqft?.toLocaleString() || "N/A"}</TableCell>
                        <TableCell>{comp.facets.bedrooms || "N/A"}</TableCell>
                        <TableCell>{comp.facets.bathrooms || "N/A"}</TableCell>
                        <TableCell>
                          <PricePerSqFtComparison
                            compPricePerSqft={pricePerSqft}
                            subjectPricePerSquareFoot={
                              subjectPricePerSquareFoot
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {new Date(comp.listingDate).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y">
              {comps.map((comp) => {
                const price =
                  comp.pricing.sold?.amount || comp.pricing.sale?.amount;
                const sqft = comp.facets.homeSize?.value;
                const pricePerSqft = price && sqft ? price / sqft : null;

                return (
                  <div key={comp.id} className="p-4 space-y-2">
                    <div className="font-medium">
                      {comp.address.displayAddress}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-muted-foreground">Price:</span>{" "}
                        {price ? formatCurrency(price) : "N/A"}
                      </div>
                      <div>
                        <span className="text-muted-foreground">Sq.Ft:</span>{" "}
                        {sqft?.toLocaleString() || "N/A"}
                      </div>
                      <div>
                        <span className="text-muted-foreground">
                          Beds/Baths:
                        </span>{" "}
                        {comp.facets.bedrooms || "N/A"}/
                        {comp.facets.bathrooms || "N/A"}
                      </div>
                      <div>
                        <span className="text-muted-foreground">$/Sq.Ft:</span>{" "}
                        <PricePerSqFtComparison
                          compPricePerSqft={pricePerSqft}
                          subjectPricePerSquareFoot={subjectPricePerSquareFoot}
                        />
                      </div>
                      <div className="col-span-2">
                        <span className="text-muted-foreground">
                          Sale Date:
                        </span>{" "}
                        {new Date(comp.listingDate).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      </div>
    </section>
  );
}
